/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import Badge from '@atlaskit/badge';
import Button, { type ButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FilterIcon from '@atlaskit/icon/core/filter';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Hide, Inline } from '@atlaskit/primitives/compiled';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useNumberOfSelectedValues } from '../../../controllers/ast/use-selected-values/index.tsx';
import messages from './messages.tsx';

type Props = {
	hasPulse?: boolean;
	isSelected?: boolean;
	isSoftwareFullTheming?: boolean;
	onClick: ButtonProps['onClick'];
	triggerProps: TriggerProps;
};

const styles = cssMap({
	tooltipContent: {
		textAlign: 'center',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		columnGap: token('space.050'),
		paddingInline: token('space.075'),
		paddingBlock: token('space.025'),
	},
	keyboardShortcutTag: {
		display: 'inline-flex',
		height: '16px',
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		justifyContent: 'center',
		alignItems: 'center',
		gap: token('space.075'),
		color: token('color.text.inverse'),
		borderColor: token('color.border.inverse'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		textAlign: 'center',
	},
	contentContainer: {
		height: '20px',
	},
});

export const FilterPopupTrigger = forwardRef<HTMLButtonElement, Props>(
	({ hasPulse, isSelected, isSoftwareFullTheming, onClick, triggerProps }, ref) => {
		const { formatMessage } = useIntl();

		const numberOfSelectedValues = useNumberOfSelectedValues();

		const formattedTooltipContent = useMemo(
			() => (
				<Box xcss={styles.tooltipContent}>
					<FormattedMessage
						{...messages.tooltipMessage}
						values={{
							inline: (parts: React.ReactNode[]) => (
								<Inline xcss={styles.keyboardShortcutTag}>{parts}</Inline>
							),
						}}
					/>
				</Box>
			),
			[],
		);

		return (
			<SpotlightPulse radius={3} pulse={hasPulse}>
				<Tooltip content={formattedTooltipContent} hideTooltipOnMouseDown>
					{(tooltipProps) => (
						<Button
							{...tooltipProps}
							{...triggerProps}
							aria-label={formatMessage(messages.filterButton)}
							interactionName="filter-popup-load-content"
							isSelected={isSelected}
							onClick={onClick}
							ref={mergeRefs(ref, triggerProps.ref)}
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							testId="filter-refinement.ui.filter-popup.button"
						>
							<Inline
								as="span"
								alignBlock="center"
								space="space.075"
								xcss={styles.contentContainer}
							>
								<FilterIcon label="" />
								<Hide as="span" below="md">
									{formatMessage(messages.filterButton)}
								</Hide>
								{numberOfSelectedValues > 0 && (
									<>
										{isSoftwareFullTheming ? (
											<span
												css={themedBadgeStyles}
												data-testid="filter-refinement.ui.filter-popup.filter-popup-trigger.badge"
											>
												{numberOfSelectedValues}
											</span>
										) : (
											<Badge
												appearance="primary"
												testId="filter-refinement.ui.filter-popup.filter-popup-trigger.badge"
											>
												{numberOfSelectedValues}
											</Badge>
										)}
									</>
								)}
							</Inline>
						</Button>
					)}
				</Tooltip>
			</SpotlightPulse>
		);
	},
);

const themedBadgeStyles = css({
	borderRadius: token('radius.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundColor: Tokens.COLOR_BACKGROUND_ACCENT_SELECTED,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: Tokens.COLOR_TEXT_ACCENT_SELECTED,
	paddingInline: token('space.050'),
	minWidth: token('space.200'),
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
	textAlign: 'center',
	font: token('font.body.small'),
	display: 'inline-flex',
	justifyContent: 'center',
	blockSize: 'min-content',
});
